import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ActionButton from './common/ActionButton';
import NeonBorder from './common/NeonBorder';
import { format } from 'date-fns';
import { TOKEN_COSTS, ensurePricingCacheFresh, loadPricingCache, getPricingCacheMeta, getModelOptions, getPricingForModel } from '../utils/tokenCounter';

const { FiX, FiDownload, FiUpload, FiRefreshCw, FiAlertTriangle, FiCode, FiDatabase, FiShield, FiTarget, FiZap, FiFileText, FiGitBranch, FiSettings } = FiIcons;

const SettingsModal = ({ isOpen, onClose, settings, onSettingsChange, onProjectBackup, onProjectImport }) => {
  const [updateStatus, setUpdateStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  // Pricing/Model-Cache (clientseitig pro Browser)
  const [pricingMeta, setPricingMeta] = useState({
    updated_at_iso: null,
    age_hours: null,
    age_days: null,
    is_older_than_24h: true,
    is_older_than_7d: true,
    error: null,
    isRefreshing: false
  });
  const [modelOptions, setModelOptions] = useState(getModelOptions());

  useEffect(() => {
    if (!isOpen) return;
    if (activeTab !== 'ai') return;

    // 1) Sofort den lokalen Cache laden (falls vorhanden)
    const cached = loadPricingCache();
    if (cached) {
      const meta = getPricingCacheMeta(cached);
      setPricingMeta(prev => ({ ...prev, ...meta, error: cached?.error || null }));
      setModelOptions(getModelOptions());
    } else {
      setModelOptions(getModelOptions());
    }

    // 2) Falls Cache aelter als 24h: Hintergrund-Refresh starten
    let cancelled = false;
    (async () => {
      try {
        const fresh = await ensurePricingCacheFresh({ force: false });
        if (cancelled) return;

        const meta = getPricingCacheMeta(fresh);
        setPricingMeta(prev => ({ ...prev, ...meta, error: fresh?.error || null }));
        setModelOptions(getModelOptions());
      } catch (e) {
        if (cancelled) return;
        setPricingMeta(prev => ({ ...prev, error: String(e) }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, activeTab]);

  const handlePricingRefresh = async () => {
    setPricingMeta(prev => ({ ...prev, isRefreshing: true, error: null }));
    try {
      const fresh = await ensurePricingCacheFresh({ force: true });
      const meta = getPricingCacheMeta(fresh);
      setPricingMeta(prev => ({ ...prev, ...meta, isRefreshing: false, error: fresh?.error || null }));
      setModelOptions(getModelOptions());
    } catch (e) {
      setPricingMeta(prev => ({ ...prev, isRefreshing: false, error: String(e) }));
    }
  };

  const getPricePer1k = (modelId, type) => {
    const pricing = getPricingForModel(modelId);
    const per1m = type === 'output' ? pricing.output_per_1m_usd : pricing.input_per_1m_usd;
    if (typeof per1m !== 'number') return null;
    return (per1m / 1000).toFixed(4);
  };

  const getContextWindowText = (modelId) => {
    const pricing = getPricingForModel(modelId);
    if (typeof pricing.context_window_tokens !== 'number') return null;
    return `${pricing.context_window_tokens.toLocaleString()} Tokens`;
  };

  if (!isOpen) return null;

  const handleBackup = () => {
    const dateStr = format(new Date(), 'yyyy-MM-dd-HHmmss');
    const filename = `meRepomix-Wrapper-Backup-${dateStr}.json`;
    
    const element = document.createElement("a");
    const file = new Blob([JSON.stringify(settings, null, 2)], {type: 'application/json'});
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    
    alert(`Einstellungen-Backup erstellt: ${filename}`);
  };

  const handleRestore = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const restoredSettings = JSON.parse(e.target.result);
        onSettingsChange(restoredSettings);
        alert('Einstellungen erfolgreich wiederhergestellt!');
      } catch (err) {
        alert('Fehler beim Wiederherstellen: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  const handleProjectBackup = () => {
    if (onProjectBackup) {
      onProjectBackup();
    }
  };

  const handleProjectImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const projectData = JSON.parse(e.target.result);
        if (onProjectImport) {
          onProjectImport(projectData);
        }
        alert('Projekt erfolgreich importiert!');
      } catch (err) {
        alert('Fehler beim Importieren des Projekts: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  const handleUpdate = () => {
    const confirmUpdate = window.confirm(
      "Möchtest du das Update starten? Deine persönlichen Einstellungen bleiben erhalten. Nur wenn es technisch nicht anders geht, fragen wir dich vorher noch einmal."
    );
    if (confirmUpdate) {
      setUpdateStatus("Update wird geladen...");
      setTimeout(() => setUpdateStatus("Das Programm ist auf dem neuesten Stand!"), 2000);
    }
  };

  const toggleSetting = (key) => {
    onSettingsChange({ ...settings, [key]: !settings[key] });
  };

  const updateSetting = (key, value) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  const tabs = [
    { id: 'general', label: 'Allgemein', icon: FiSettings },
    { id: 'output', label: 'Ausgabe', icon: FiFileText },
    { id: 'ai', label: 'KI-Optimierung', icon: FiTarget },
    { id: 'security', label: 'Sicherheit', icon: FiShield },
    { id: 'advanced', label: 'Erweitert', icon: FiZap }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <NeonBorder className="w-full max-w-4xl bg-white shadow-2xl rounded-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 bg-white border-b border-me-blue/10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-me-blue">Einstellungen</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-me-blue">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'bg-me-orange text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <SafeIcon icon={tab.icon} />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div className="p-4 bg-me-bg rounded-lg border border-me-orange/20">
                <h3 className="text-lg font-semibold text-me-blue mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiDownload} /> Projekt-Sicherung (Backup)
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Speichere dein komplettes Projekt inklusive aller Dateien und Einstellungen.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <ActionButton onClick={handleProjectBackup} variant="primary">
                      <SafeIcon icon={FiDownload} />
                      Projekt sichern
                    </ActionButton>
                  </div>
                  <div>
                    <input 
                      type="file" 
                      accept=".json"
                      onChange={handleProjectImport}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-me-orange/10 file:text-me-orange hover:file:bg-me-orange/20 cursor-pointer"
                    />
                    <p className="text-xs text-gray-500 mt-2">Projekt-Backup importieren</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-me-bg rounded-lg border border-me-orange/20">
                <h3 className="text-lg font-semibold text-me-blue mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiSettings} /> Einstellungen-Sicherung
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Speichere nur deine persönlichen Einstellungen.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <ActionButton onClick={handleBackup} variant="secondary">
                      Einstellungen sichern
                    </ActionButton>
                  </div>
                  <div>
                    <input 
                      type="file" 
                      accept=".json"
                      onChange={handleRestore}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-me-blue/10 file:text-me-blue hover:file:bg-me-blue/20 cursor-pointer"
                    />
                    <p className="text-xs text-gray-500 mt-2">Einstellungen importieren</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-me-bg rounded-lg border border-me-orange/20">
                <h3 className="text-lg font-semibold text-me-blue mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiRefreshCw} /> Updates (Aktualisierungen)
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Hol dir die neuesten Funktionen und Verbesserungen.
                </p>
                <ActionButton onClick={handleUpdate} variant="primary">
                  Nach Updates suchen
                </ActionButton>
                {updateStatus && (
                  <p className="mt-2 text-me-orange font-medium animate-pulse">
                    {updateStatus}
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'output' && (
            <div className="space-y-6">
              <div className="p-4 bg-me-bg rounded-lg border border-me-orange/20">
                <h3 className="text-lg font-semibold text-me-blue mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiFileText} /> Ausgabe-Formatierung
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-me-blue">ASCII-Baumstruktur</h4>
                      <p className="text-sm text-gray-500">Automatische Projektstruktur als ASCII-Baum</p>
                    </div>
                    <button
                      onClick={() => toggleSetting('includeAsciiTree')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        settings.includeAsciiTree ? 'bg-me-orange' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        settings.includeAsciiTree ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-me-blue">Symbol-Index</h4>
                      <p className="text-sm text-gray-500">Inhaltsverzeichnis mit Klassen und Funktionen</p>
                    </div>
                    <button
                      onClick={() => toggleSetting('includeSymbolIndex')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        settings.includeSymbolIndex ? 'bg-me-orange' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        settings.includeSymbolIndex ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-me-blue">API-Endpunkt-Map</h4>
                      <p className="text-sm text-gray-500">Liste aller API-Routen im Projekt</p>
                    </div>
                    <button
                      onClick={() => toggleSetting('includeApiMap')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        settings.includeApiMap ? 'bg-me-orange' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        settings.includeApiMap ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-me-blue">Datenbank-Schema</h4>
                      <p className="text-sm text-gray-500">Automatische Erkennung von SQL-Tabellen</p>
                    </div>
                    <button
                      onClick={() => toggleSetting('includeDbSchema')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        settings.includeDbSchema ? 'bg-me-orange' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        settings.includeDbSchema ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-me-bg rounded-lg border border-me-orange/20">
                <h3 className="text-lg font-semibold text-me-blue mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiCode} /> Code-Optimierung
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-me-blue">Smart-Squeeze</h4>
                      <p className="text-sm text-gray-500">Kommentare und Leerraum entfernen (Token sparen)</p>
                    </div>
                    <button
                      onClick={() => toggleSetting('smartSqueeze')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        settings.smartSqueeze ? 'bg-me-orange' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        settings.smartSqueeze ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  <div>
                    <h4 className="font-medium text-me-blue mb-2">Aufgaben-Beschreibung (Task Prompt)</h4>
                    <p className="text-sm text-gray-500 mb-2">Beschreibe, was die KI tun soll</p>
                    <textarea
                      value={settings.taskPrompt || ''}
                      onChange={(e) => updateSetting('taskPrompt', e.target.value)}
                      placeholder="z.B. 'Analysiere den Code und schlage Verbesserungen vor'"
                      className="w-full p-3 border border-me-blue/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-me-orange/50 resize-none"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="space-y-6">
              <div className="p-4 bg-me-bg rounded-lg border border-me-orange/20">
                <h3 className="text-lg font-semibold text-me-blue mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiTarget} /> KI-Modell-Einstellungen
                </h3>

                <div className="flex flex-col gap-2 mb-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <ActionButton onClick={handlePricingRefresh} disabled={pricingMeta.isRefreshing}>
                      <SafeIcon icon={FiRefreshCw} /> {pricingMeta.isRefreshing ? 'Aktualisiere…' : 'Preise aktualisieren'}
                    </ActionButton>

                    <span className="text-sm text-gray-600">
                      {pricingMeta.updated_at_iso ? (
                        <>Cache: {new Date(pricingMeta.updated_at_iso).toLocaleString()} ({typeof pricingMeta.age_hours === 'number' ? pricingMeta.age_hours.toFixed(1) : '?'}h alt)</>
                      ) : (
                        <>Noch kein Preis-Cache geladen.</>
                      )}
                    </span>
                  </div>

                  {pricingMeta.is_older_than_7d && (
                    <div className="text-sm text-me-orange flex items-center gap-2">
                      <SafeIcon icon={FiAlertTriangle} />
                      Preis-Cache ist aelter als 7 Tage. Bitte aktualisieren (oder Internet pruefen).
                    </div>
                  )}

                  {pricingMeta.error && (
                    <div className="text-sm text-red-600">
                      Update-Fehler: {pricingMeta.error}
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block font-medium text-me-blue mb-2">Ziel-Modell (Target Model)</label>
                    <select
                      value={settings.targetModel || 'gpt-4'}
                      onChange={(e) => updateSetting('targetModel', e.target.value)}
                      className="w-full p-3 border border-me-blue/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-me-orange/50 bg-white"
                    >
                      {modelOptions.map((m) => (
                        <option key={m.id} value={m.id}>{m.label}</option>
                      ))}
                    </select>
                    <p className="text-sm text-gray-500 mt-2">
                      Kosten: Input ${getPricePer1k(settings.targetModel || 'gpt-4', 'input') ?? 'N/A'} / 1k Tok • Output ${getPricePer1k(settings.targetModel || 'gpt-4', 'output') ?? 'N/A'} / 1k Tok
                    </p>
                  </div>

                  <div>
                    <label className="block font-medium text-me-blue mb-2">Formatierung</label>
                    <select
                      value={settings.outputFormat || 'markdown'}
                      onChange={(e) => updateSetting('outputFormat', e.target.value)}
                      className="w-full p-3 border border-me-blue/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-me-orange/50 bg-white"
                    >
                      <option value="markdown">Markdown</option>
                      <option value="xml">XML (für Claude)</option>
                      <option value="json">JSON</option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-medium text-me-blue mb-2">Token-Limit</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={settings.tokenLimit || 100000}
                        onChange={(e) => updateSetting('tokenLimit', parseInt(e.target.value))}
                        className="flex-1 p-3 border border-me-blue/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-me-orange/50"
                      />
                      <select
                        value={settings.tokenLimitUnit || 'tokens'}
                        onChange={(e) => updateSetting('tokenLimitUnit', e.target.value)}
                        className="p-3 border border-me-blue/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-me-orange/50 bg-white"
                      >
                        <option value="tokens">Tokens</option>
                        <option value="chars">Zeichen</option>
                      </select>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">Bei Überschreitung wird automatisch in mehrere Dateien aufgeteilt (Chunking)</p>
                    {getContextWindowText(settings.targetModel || 'gpt-4') && (
                      <p className="text-sm text-gray-500 mt-1">
                        Kontextfenster (Token-Limit) des Modells: {getContextWindowText(settings.targetModel || 'gpt-4')}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-me-blue">Live-Token-Schätzung</h4>
                      <p className="text-sm text-gray-500">Zeigt voraussichtliche Token-Kosten an</p>
                    </div>
                    <button
                      onClick={() => toggleSetting('liveTokenEstimation')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        settings.liveTokenEstimation ? 'bg-me-orange' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        settings.liveTokenEstimation ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-me-bg rounded-lg border border-me-orange/20">
                <h3 className="text-lg font-semibold text-me-blue mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiDatabase} /> Kontext-Anreicherung
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-me-blue">Dependency-Crawler</h4>
                      <p className="text-sm text-gray-500">Automatisch alle Import-Dateien einschließen</p>
                    </div>
                    <button
                      onClick={() => toggleSetting('dependencyCrawler')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        settings.dependencyCrawler ? 'bg-me-orange' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        settings.dependencyCrawler ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-me-blue">Tech-Stack-Autodetect</h4>
                      <p className="text-sm text-gray-500">Automatisch Frameworks und Tools erkennen</p>
                    </div>
                    <button
                      onClick={() => toggleSetting('techStackAutodetect')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        settings.techStackAutodetect ? 'bg-me-orange' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        settings.techStackAutodetect ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              <div className="p-4 bg-me-bg rounded-lg border border-me-orange/20">
                <h3 className="text-lg font-semibold text-me-blue mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiShield} /> Datenschutz & Sicherheit
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-me-blue">PII-Scrubber</h4>
                      <p className="text-sm text-gray-500">E-Mails, IP-Adressen und API-Keys entfernen</p>
                    </div>
                    <button
                      onClick={() => toggleSetting('piiScrubber')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        settings.piiScrubber ? 'bg-me-orange' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        settings.piiScrubber ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-yellow-700">
                        Der PII-Scrubber entfernt automatisch persönliche Daten wie E-Mail-Adressen, IP-Adressen und API-Schlüssel aus dem Output.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'advanced' && (
            <div className="space-y-6">
              <div className="p-4 bg-me-bg rounded-lg border border-me-orange/20">
                <h3 className="text-lg font-semibold text-me-blue mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiZap} /> Erweiterte Optionen
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-me-blue">Git-Awareness</h4>
                      <p className="text-sm text-gray-500">Nur geänderte Dateien bündeln (--changed)</p>
                    </div>
                    <button
                      onClick={() => toggleSetting('gitAwareness')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        settings.gitAwareness ? 'bg-me-orange' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        settings.gitAwareness ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-me-blue">Clipboard-Export</h4>
                      <p className="text-sm text-gray-500">Ergebnis direkt in die Zwischenablage kopieren</p>
                    </div>
                    <button
                      onClick={() => toggleSetting('clipboardExport')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        settings.clipboardExport ? 'bg-me-orange' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        settings.clipboardExport ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-me-blue">Incremental Mode</h4>
                      <p className="text-sm text-gray-500">Nur Änderungen seit letztem Export senden</p>
                    </div>
                    <button
                      onClick={() => toggleSetting('incrementalMode')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        settings.incrementalMode ? 'bg-me-orange' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        settings.incrementalMode ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  <div>
                    <label className="block font-medium text-me-blue mb-2">Fokus-Preset</label>
                    <select
                      value={settings.focusPreset || 'all'}
                      onChange={(e) => updateSetting('focusPreset', e.target.value)}
                      className="w-full p-3 border border-me-blue/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-me-orange/50 bg-white"
                    >
                      <option value="all">Alle Dateien</option>
                      <option value="frontend">Frontend (.ts, .tsx, .css, .jsx)</option>
                      <option value="backend">Backend (.py, .go, .java, .sql)</option>
                      <option value="docs">Dokumentation (.md, .txt, .rst)</option>
                    </select>
                    <p className="text-sm text-gray-500 mt-2">Filtert Dateien nach Typ</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </NeonBorder>
    </div>
  );
};

export default SettingsModal;